import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { BranchRole, HasItems, LocationGroupRole, LocationRole, SystemRole } from '../types';
import { ApiKey, CreateApiKeyRequest, GetApiKeysOptions, UpdateApiKeyRequest } from '../types/api-keys';

@Injectable({
  providedIn: 'root',
})
export class ApiKeyService {
  private http = inject(HttpClient);
  private apiHost = `${environment.apiBaseUrl}`;

  createApiKey(request: CreateApiKeyRequest): Observable<ApiKey> {
    return this.http.post<ApiKey>(`${this.apiHost}/ApiKeys`, request);
  }

  updateApiKey(key: string, request: UpdateApiKeyRequest): Observable<ApiKey> {
    return this.http.put<ApiKey>(`${this.apiHost}/ApiKeys/${key}`, request);
  }

  // This endpoint is used to enable/disable an api key,
  // after disabling a key the GET endpoint won't return it unless specified in the query.
  // We don't actually delete keys
  updateApiKeyActiveStatus(key: string, isActive: boolean): Observable<ApiKey> {
    return this.http.put<ApiKey>(`${this.apiHost}/ApiKeys/${key}/Status`, { is_active: isActive });
  }

  getApiKeys(options?: GetApiKeysOptions): Observable<HasItems<ApiKey>> {
    let params = new HttpParams();
    if (options?.owner_name) {
      params = params.set('owner_name', options.owner_name);
    }
    if (options?.email) {
      params = params.set('email', options.email);
    }
    return this.http.get<HasItems<ApiKey>>(`${this.apiHost}/ApiKeys`, { params });
  }

  grantSystemRole(key: string, role: SystemRole): Observable<ApiKey> {
    return this.http.put<ApiKey>(`${this.apiHost}/ApiKeys/${key}/Roles/${role}`, {});
  }

  revokeSystemRole(key: string, role: SystemRole): Observable<ApiKey> {
    return this.http.delete<ApiKey>(`${this.apiHost}/ApiKeys/${key}/Roles/${role}`);
  }

  grantBranchRole(key: string, branchId: number, role: BranchRole): Observable<ApiKey> {
    return this.http.put<ApiKey>(`${this.apiHost}/ApiKeys/${key}/Branches/${branchId}/Roles/${role}`, {});
  }

  revokeBranchRole(key: string, branchId: number, role: BranchRole): Observable<ApiKey> {
    return this.http.delete<ApiKey>(`${this.apiHost}/ApiKeys/${key}/Branches/${branchId}/Roles/${role}`);
  }

  grantLocationRole(key: string, locationId: number, role: LocationRole): Observable<ApiKey> {
    return this.http.put<ApiKey>(`${this.apiHost}/ApiKeys/${key}/Locations/${locationId}/Roles/${role}`, {});
  }

  revokeLocationRole(key: string, locationId: number, role: LocationRole): Observable<ApiKey> {
    return this.http.delete<ApiKey>(`${this.apiHost}/ApiKeys/${key}/Locations/${locationId}/Roles/${role}`);
  }

  // I don't expect these 2 to be used atm but leaving them here just in case
  grantLocationGroupRole(key: string, locationGroupId: string, role: LocationGroupRole): Observable<ApiKey> {
    return this.http.put<ApiKey>(`${this.apiHost}/ApiKeys/${key}/LocationGroups/${locationGroupId}/Roles/${role}`, {});
  }
  revokeLocationGroupRole(key: string, locationGroupId: string, role: LocationGroupRole): Observable<ApiKey> {
    return this.http.delete<ApiKey>(`${this.apiHost}/ApiKeys/${key}/LocationGroups/${locationGroupId}/Roles/${role}`);
  }
}
